import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { tenantApi } from "@/lib/api/client";
import { PRINT_SUMMARY_DAYS, formatCost, printSummaryKey } from "@/lib/print";
import {
  Badge,
  Button,
  Card,
  Text,
  Divider,
  Progress,
  Stack,
  Box,
  Group,
  SimpleGrid,
  Table,
} from "@mantine/core";
import { SectionGuides } from "@/components/guides/section-guides";
import { StartPrintingCard } from "@/components/print/start-printing-card";
import { useStartPrinting } from "@/hooks/use-start-printing";

/** Window shown by both the summary card and the job table below it. */
const SUMMARY_DAYS = PRINT_SUMMARY_DAYS;

export default function PrintPage() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["print-quota"],
    queryFn: () => tenantApi.getPrintQuota(),
  });

  const { data: summaryData, isLoading: summaryLoading } = useQuery({
    queryKey: printSummaryKey(SUMMARY_DAYS),
    queryFn: () => tenantApi.getPrintSummary(SUMMARY_DAYS),
  });

  const startDate = useMemo(() => {
    const from = new Date();
    from.setDate(from.getDate() - SUMMARY_DAYS);
    return from.toISOString().slice(0, 10);
  }, []);

  const { data: historyData } = useQuery({
    queryKey: ["print-history", startDate],
    queryFn: () => tenantApi.getPrintHistory(1, 50, startDate),
  });

  const startPrinting = useStartPrinting();

  const quota = data?.data;
  const summary = summaryData?.data;
  const jobs = historyData?.data.results ?? [];
  // The site can hide prices entirely; the backend then zeroes/nulls the
  // amounts, so rendering them would show a misleading "Free" everywhere.
  const showPrices = summary?.show_prices ?? false;
  const onboarded = summary?.has_print_account ?? false;

  if (isLoading || summaryLoading) return <Box p="md">Loading...</Box>;

  if (isError) {
    return (
      <Box p="md">
        <Text fz="sm" c="red">Failed to load data. Please try again later.</Text>
      </Box>
    );
  }

  // Never onboarded: no account, no history, nothing installed — an empty
  // dashboard would be noise. Sell the setup instead.
  if (!onboarded) {
    return (
      <Stack p="md" gap="xl" align="center">
        <SectionGuides area="print" />
        <Box mt="lg" w="100%" maw={560}>
          <StartPrintingCard />
        </Box>
      </Stack>
    );
  }

  return (
    <Stack p="md" gap="xl">
      <SectionGuides area="print" />
      <Card padding="lg" withBorder>
        <Stack gap="sm">
          <Group justify="space-between" align="center">
            <Text fw={600} fz="lg">Print account</Text>
            <Button
              size="sm"
              variant="light"
              loading={startPrinting.isPending}
              onClick={() => startPrinting.mutate()}
            >
              Resend instructions
            </Button>
          </Group>
          <Text fz="sm" c="dimmed">
            Your print account is active. Need the setup email for a new device? Get it again any time.
          </Text>
        </Stack>
      </Card>
      {/* Sites without a configured quota simply don't get the card. */}
      {quota?.quota_amount ? (
        <Card padding="lg" withBorder>
          <Stack gap="md">
            <Text fw={600} fz="lg">Print Quota</Text>
            <Box ta="center" mb="md">
              <Text fz={32} fw={700}>
                {quota.remaining_amount}
              </Text>
              <Text c="dimmed">remaining</Text>
            </Box>
            <Progress
              value={((quota.used_amount || 0) / quota.quota_amount) * 100}
              size="sm"
            />
            <Group justify="space-between" mt="xs">
              <Text fz="sm" c="dimmed">0</Text>
              <Text fz="sm" c="dimmed">{quota.quota_amount}</Text>
            </Group>
            <Divider my="md" />
            <SimpleGrid cols={2} spacing="md">
              <Box ta="center">
                <Text fz="xl" fw={700}>{quota.used_amount || 0}</Text>
                <Text fz="sm" c="dimmed">Used</Text>
              </Box>
              <Box ta="center">
                <Text fz="xl" fw={700}>{quota.quota_amount}</Text>
                <Text fz="sm" c="dimmed">Quota</Text>
              </Box>
            </SimpleGrid>
          </Stack>
        </Card>
      ) : null}
      <Card padding="lg" withBorder>
        <Stack gap="md">
          <Text fw={600} fz="lg">Last {SUMMARY_DAYS} days</Text>
          <SimpleGrid cols={{ base: 2, sm: showPrices ? 5 : 4 }} spacing="md">
            <Box ta="center">
              <Text fz="xl" fw={700}>{summary?.total_pages ?? 0}</Text>
              <Text fz="sm" c="dimmed">Pages printed</Text>
            </Box>
            <Box ta="center">
              <Text fz="xl" fw={700}>{summary?.color_pages ?? 0}</Text>
              <Text fz="sm" c="dimmed">Colour pages</Text>
            </Box>
            <Box ta="center">
              <Text fz="xl" fw={700}>{summary?.bw_pages ?? 0}</Text>
              <Text fz="sm" c="dimmed">B&amp;W pages</Text>
            </Box>
            <Box ta="center">
              <Text fz="xl" fw={700}>{summary?.print_jobs_count ?? 0}</Text>
              <Text fz="sm" c="dimmed">Print jobs</Text>
            </Box>
            {showPrices && (
              <Box ta="center">
                <Text fz="xl" fw={700}>{formatCost(summary?.total_cost, summary)}</Text>
                <Text fz="sm" c="dimmed">Cost</Text>
              </Box>
            )}
          </SimpleGrid>
        </Stack>
      </Card>
      <Card padding="lg" withBorder>
        <Stack gap="md">
          <Text fw={600} fz="lg">Recent print jobs</Text>
          {jobs.length === 0 ? (
            <Text ta="center" c="dimmed">No print jobs in the last {SUMMARY_DAYS} days</Text>
          ) : (
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Document</Table.Th>
                  <Table.Th>Printer</Table.Th>
                  <Table.Th>Pages</Table.Th>
                  <Table.Th>Type</Table.Th>
                  {showPrices && <Table.Th>Cost</Table.Th>}
                  <Table.Th>Date</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {jobs.map((job) => (
                  <Table.Tr key={job.id}>
                    <Table.Td fw={500}>{job.document_name}</Table.Td>
                    <Table.Td c="dimmed">{job.printer_name}</Table.Td>
                    <Table.Td>{job.pages}</Table.Td>
                    <Table.Td>
                      <Badge variant={job.is_color ? "filled" : "light"}>
                        {job.is_color ? "Color" : "B&W"}
                      </Badge>
                    </Table.Td>
                    {showPrices && <Table.Td>{formatCost(job.cost, summary)}</Table.Td>}
                    <Table.Td c="dimmed" fz="xs">
                      {new Date(job.job_time).toLocaleString()}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
